import type { TaxonomyAuditResponseRow } from '@risksmart-app/drizzle/src/dto';
import type { GetTaxonomyAuditQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetTaxonomyAuditDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

type UseGetTaxonomyAuditArgs = {
  locale: string;
  orgKey: string;
};

/**
 * Taxonomy version history for the settings tab (RSP-6214). The tRPC procedure
 * scopes by the caller's org server-side; `orgKey` only feeds the legacy
 * GraphQL `where` on the flag-off Apollo branch.
 */
export const useGetTaxonomyAudit = createQueryHook<
  UseGetTaxonomyAuditArgs,
  TaxonomyAuditResponseRow[],
  GetTaxonomyAuditQuery
>({
  trpcQueryOptions: (trpc, { locale }) =>
    trpc.frontend.taxonomy.taxonomyAudit.queryOptions({ locale }),
  // The per-version org-usage count is resolved once server-side
  // (`OrganisationCount`) and re-expanded into the legacy
  // `organisations_aggregate.aggregate.count` shape each row carries.
  mapTrpcDataToGraphQL: (data) => ({
    taxonomy_audit: (data ?? []).map((row) => ({
      Description: row.Description,
      Id: row.Id,
      Common: row.Common,
      Library: row.Library,
      Rating: row.Rating,
      Taxonomy: row.Taxonomy,
      InternalAuditRating: row.InternalAuditRating,
      OversightRating: row.OversightRating,
      ModifiedAtTimestamp: row.ModifiedAtTimestamp,
      organisations_aggregate: {
        aggregate: { count: row.OrganisationCount },
      },
    })),
  }),
  graphqlDocument: GetTaxonomyAuditDocument,
  graphqlVariables: ({ locale, orgKey }) => ({
    Locale: locale,
    OrgKey: orgKey,
  }),
});
